import { z } from "zod";

/**
 * Status of a prospect/offer/mapBusiness email address. Stored as a free-form string on the
 * models (`emailStatus` column) — this module is the single source of truth for the values and
 * the semantics that branch on them, shared between API and web.
 *
 * - `VALID`    – an email was found and verified; safe to send.
 * - `RISKY`    – a verification answered catch-all or unknown: the mailbox may exist, the server
 *                will not say. Sendable (it counts as "has email" everywhere), not terminal and not
 *                bounced. Written by user-provided email verification (PREC-4265).
 * - `INVALID`  – an email-find attempt failed verification; the stored email (if any) is cleared.
 * - `RETRY`    – an email-find attempt could not run (provider outage, or no domain/name to ask
 *                with); it says nothing about the address, so it is re-attempted on the next cycle.
 *                A lookup that ran and definitively found nothing is `INVALID`.
 * - `SEARCHING`– a find is currently in flight.
 * - `BOUNCED_VALID`   – a sent email hard/soft-bounced, BUT a fresh ZeroBounce check still reports
 *                       the address as valid. Likely a transient bounce (full mailbox, greylisting,
 *                       temporary block). The address is kept so the user can decide.
 * - `BOUNCED_INVALID` – a sent email bounced AND a fresh ZeroBounce check no longer reports it as
 *                       valid. Treat the address as dead. The address is kept for reference.
 *
 * `BOUNCED_VALID`/`BOUNCED_INVALID` are set by the inbound-bounce handler (`checkForBounce`).
 */
export const EMAIL_STATUSES = [
	"VALID",
	"RISKY",
	"INVALID",
	"RETRY",
	"SEARCHING",
	"BOUNCED_VALID",
	"BOUNCED_INVALID",
] as const;

export type EmailStatus = (typeof EMAIL_STATUSES)[number];

export const emailStatusSchema = z.enum(EMAIL_STATUSES);

/**
 * Narrows a stored `emailStatus` (a free-form string column) to the shared union, or `null` when
 * the value is not a known status (including no status at all). Display mappings switch on the
 * result so a new member cannot silently inherit an old state's badge (PREC-5035).
 */
export const toEmailStatus = (status?: string | null): EmailStatus | null => {
	const parsed = emailStatusSchema.safeParse(status);
	return parsed.success ? parsed.data : null;
};

/**
 * Compile-time exhaustiveness guard for a `switch` over {@link EmailStatus}: if a new member is
 * added without a case, its branch's `default` call fails to typecheck.
 */
export const unhandledEmailStatus = (status: never): never => {
	throw new Error(`Unhandled email status: ${String(status)}`);
};

/**
 * Statuses set when an email bounced. Both keep the stored address; they differ only in whether a
 * post-bounce re-verification still considers it valid.
 */
export const BOUNCED_EMAIL_STATUSES = ["BOUNCED_VALID", "BOUNCED_INVALID"] as const;

/**
 * Terminal statuses for the email-find pipeline: once a prospect is in one of these, re-finding is
 * skipped (it would waste API calls and, for bounced addresses, clobber the bounce result). Does
 * NOT include `RETRY`/`SEARCHING`, which are transient and meant to be re-attempted.
 */
export const TERMINAL_EMAIL_STATUSES = ["INVALID", ...BOUNCED_EMAIL_STATUSES] as const;

export const isBouncedEmailStatus = (status?: string | null): boolean =>
	!!status && (BOUNCED_EMAIL_STATUSES as readonly string[]).includes(status);

export const isTerminalEmailStatus = (status?: string | null): boolean =>
	!!status && (TERMINAL_EMAIL_STATUSES as readonly string[]).includes(status);

/**
 * Statuses whose stored address counts as an email the lead has: every "has email" reader (the
 * campaign has-email condition, the lookup pre-flights that skip a paid search, the lead's email
 * chip) asks {@link isSendableEmailStatus} rather than comparing with `"VALID"`, so a `RISKY`
 * address never reads as "no email" and never starts a new paid lookup (PREC-4265).
 */
export const SENDABLE_EMAIL_STATUSES = ["VALID", "RISKY"] as const satisfies readonly EmailStatus[];

export const isSendableEmailStatus = (status?: string | null): boolean =>
	!!status && (SENDABLE_EMAIL_STATUSES as readonly string[]).includes(status);
